import type { GetStaticProps } from 'next';
import React from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { Logo, Wrap, ToastProvider } from '@leafwell/components';
import Blocks from 'components/blocks';
import HeroCard from 'components/hero-card';
import Loader from 'components/loader';
import { getUsLandingPage } from 'lib/queries/us-landings';
import { PageProps } from 'lib/queries/pages';

const UsLandingHome: React.FC<PageProps> = ({
  page: {
    modified,
    acf: { hero, redirectCannabisCounseling, blocks, pageType, states },
  },
  partnersReviews,
}) => {
  const siteTitle = 'Leafwell';

  const { isFallback } = useRouter();
  const cardPageSettings = {
    affiliateWebsite: null,
    affiliateLogo: null,
    formUrl: null,
    vpdPage: null,
    pauseAppointments: false,
    redirectMedicalCard: true,
    redirectProductEndpoint: null,
    totalMedicalCardPrice: null,
    utmParams: {
      utm_campaign: 'mc_embed',
      utm_medium: 'satellite',
      utm_source: 'leafwell',
    },
    name: null,
    description: null,
    imageObjectCaption: null,
    product: {
      name: null,
      description: null,
      imageUrl: null,
    },
    isRenewal: false,
    stateProductImage: null,
    vpdPartnerName: null,
    vpdLogo: null,
    price: {
      price: null,
      newPrice: null,
      percentageDiscount: null,
      discountCode: null,
    },
    customStateCode: null,
  };

  if (isFallback) {
    return <Loader />;
  }

  return (
    <>
      <NextSeo
        key="us-landing-home"
        title={siteTitle}
        nofollow={true}
        noindex={true}
        robotsProps={{ noarchive: true }}
      />
      {process.env.NODE_ENV === 'production' ? (
        <>
          <Script
            async
            src="https://www.googletagmanager.com/gtag/js?id=G-13L6MSSZMY"
          />
          <Script
            async
            src="https://www.googletagmanager.com/gtag/js?id=AW-10889710135"
          />
          <Script
            id="google-tag-init"
            dangerouslySetInnerHTML={{
              __html: `window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-13L6MSSZMY');
                gtag('config', 'AW-10889710135');`,
            }}
          />
          <Script
            id="google-ads-conversion-tracking"
            dangerouslySetInnerHTML={{
              __html: `function gtag_report_conversion() {
                gtag('event', 'conversion', {
                    'send_to': 'AW-10889710135/ODaCCO6PxZcaELeUz8go'
                });
                return false;
              }`,
            }}
          />
          <Script
            id="facebook-ad-script"
            dangerouslySetInnerHTML={{
              __html: `!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;
                n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '407701394211323');
                fbq('track', 'PageView');`,
            }}
          />
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: 'none' }}
              src="https://www.facebook.com/tr?id=407701394211323&ev=PageView&noscript=1"
            />
          </noscript>
          <Script
            id="bing-ads-conversion-tracking"
            dangerouslySetInnerHTML={{
              __html: `!(function(w,d,t,r,u)
                {
                    var f,n,i;
                    w[u]=w[u]||[],f=function()
                    {
                        var o={ti:"97187066", enableAutoSpaTracking: true};
                        o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")
                    },
                    n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function()
                    {
                        var s=this.readyState;
                        s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)
                    },
                    i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)
                })
                (window,document,"script","//bat.bing.com/bat.js","uetq");`,
            }}
          />
          <Script
            id="outbrain-ad-script"
            dangerouslySetInnerHTML={{
              __html: `!function(_window, _document) {
                var OB_ADV_ID = '00546ac78c14132ef08738da273680f113';
                if (_window.obApi) {
                  var toArray = function(object) {
                    return Object.prototype.toString.call(object) === '[object Array]' ? object : [object];
                  };
                  _window.obApi.marketerId = toArray(_window.obApi.marketerId).concat(toArray(OB_ADV_ID));
                  return;
                }
                var api = _window.obApi = function() {
                  api.dispatch ? api.dispatch.apply(api, arguments) : api.queue.push(arguments);
                };
                api.version = '1.1';
                api.loaded = true;
                api.marketerId = OB_ADV_ID;
                api.queue = [];
                var tag = _document.createElement('script');
                tag.async = true;
                tag.src = '//amplify.outbrain.com/cp/obtp.js';
                tag.type = 'text/javascript';
                var script = _document.getElementsByTagName('script')[0];
                script.parentNode.insertBefore(tag, script);
              }(window, document);
              obApi('track', 'PAGE_VIEW');`,
            }}
          />
        </>
      ) : null}
      <ToastProvider>
        <Wrap className="!py-6">
          <Logo
            {...{
              classNameButton: 'w-32 xl:w-40 z-10 lg:z-20',
              href: '',
              title: siteTitle,
            }}
          />
        </Wrap>
        <HeroCard
          hero={hero}
          states={[]}
          spacer={false}
          redirectStatePage={true}
          pageType={pageType}
          formUrl={cardPageSettings.formUrl}
          affiliateLogo={cardPageSettings.affiliateLogo}
          affiliateWebsite={cardPageSettings.affiliateWebsite}
          pauseAppointments={cardPageSettings.pauseAppointments}
          redirectMedicalCard={cardPageSettings.redirectMedicalCard}
          redirectCannabisCounseling={redirectCannabisCounseling}
          totalMedicalCardPrice={cardPageSettings.totalMedicalCardPrice}
          showTabs={false}
        />
        {blocks.length > 0 ? (
          <Blocks
            blocks={blocks}
            modified={modified}
            pageType={pageType}
            states={states}
            partnersReviews={partnersReviews}
            showTabs={false}
          />
        ) : null}
      </ToastProvider>
    </>
  );
};

export const getStaticProps: GetStaticProps = async context => {
  return await getUsLandingPage(context);
};

export default UsLandingHome;
